import React, { useEffect, useMemo, useState } from 'react';
import FormSelectInput from 'common/FormSelectInput';
import FormTextInput from 'common/FormTextInput';
import FormModal from 'common/FormModal';
import Button from 'common/Button';
import ButtonSecondary from 'common/ButtonSecondary';
import FormStyle from 'common/FormStyle';
import { Grid, Box } from '@mui/material';

const resetLinkStyle = {
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    color: '#184353',
    fontSize: '12px',
    fontWeight: 500,
    fontFamily: 'Poppins, sans-serif',
    textDecoration: 'underline',
};

const FiltersBar = ({
    open,
    onClose,
    providers = [],
    selectedProviderId,
    setSelectedProviderId,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    selectedPaymentType,
    setSelectedPaymentType,
    selectedStatus,
    setSelectedStatus,
}) => {
    const providerOptions = useMemo(() => {
        return providers;
    }, [providers]);

    const paymentTypeOptions = [
        { id: 'invoice', name: 'Invoice' },
        { id: 'consultation', name: 'Consultation' },
        { id: 'service', name: 'Service' },
    ];

    const statusOptions = [
        { id: 'paid', name: 'Paid' },
        { id: 'unpaid', name: 'Unpaid' },
        { id: 'pending', name: 'Pending' },
        { id: 'refunded', name: 'Refunded' },
        { id: 'cancelled', name: 'Cancelled' },
    ];

    // Edits are held locally until "Apply Filters" is clicked, so closing the
    // drawer (Cancel / outside click) discards changes instead of applying them.
    const [draftProviderId, setDraftProviderId] = useState(selectedProviderId);
    const [draftDateFrom, setDraftDateFrom] = useState(dateFrom);
    const [draftDateTo, setDraftDateTo] = useState(dateTo);
    const [draftPaymentType, setDraftPaymentType] = useState(selectedPaymentType);
    const [draftStatus, setDraftStatus] = useState(selectedStatus);

    // Re-sync the draft with the currently applied filters each time the drawer opens.
    useEffect(() => {
        if (open) {
            setDraftProviderId(selectedProviderId);
            setDraftDateFrom(dateFrom);
            setDraftDateTo(dateTo);
            setDraftPaymentType(selectedPaymentType);
            setDraftStatus(selectedStatus);
        }
    }, [open]);

    const hasActiveFilters =
        draftProviderId || draftDateFrom || draftDateTo || draftPaymentType || draftStatus;

    const handleApply = () => {
        setSelectedProviderId(draftProviderId || null);
        setDateFrom(draftDateFrom || null);
        setDateTo(draftDateTo || null);
        setSelectedPaymentType(draftPaymentType || null);
        setSelectedStatus(draftStatus || null);
        onClose();
    };

    const handleReset = () => {
        setDraftProviderId(null);
        setDraftDateFrom(null);
        setDraftDateTo(null);
        setDraftPaymentType(null);
        setDraftStatus(null);
    };

    const stickyFooter = (
        <Box sx={{ padding: { xs: '12px 24px', sm: '16px 40px' } }}>
            {/* MOBILE */}
            <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', gap: 1.5 }}>
                <Button type="button" onClick={handleApply} className="w-full min-h-[36px]">
                    Apply Filters
                </Button>
                <ButtonSecondary onClick={onClose} className="w-full min-h-[36px]">
                    Cancel
                </ButtonSecondary>
                {hasActiveFilters && (
                    <Box sx={{ textAlign: 'center' }}>
                        <button type="button" onClick={handleReset} style={resetLinkStyle}>
                            Reset filters
                        </button>
                    </Box>
                )}
            </Box>

            {/* DESKTOP */}
            <Box
                sx={{
                    display: { xs: 'none', sm: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                }}
            >
                {hasActiveFilters ? (
                    <button type="button" onClick={handleReset} style={resetLinkStyle}>
                        Reset filters
                    </button>
                ) : (
                    <span />
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'flex-end' }}>
                    <ButtonSecondary onClick={onClose}>Cancel</ButtonSecondary>
                    <Button
                        type="button"
                        onClick={handleApply}
                        className="flex items-center justify-center min-h-[40px]"
                    >
                        Apply Filters
                    </Button>
                </Box>
            </Box>
        </Box>
    );

    return (
        <FormModal open={open} onClose={onClose} title="Filters" stickyFooter={stickyFooter}>
            <FormStyle>
                <Grid container spacing={0} sx={{ marginTop: '8px' }}>
                    {/* Provider / Business Filter */}
                    <FormSelectInput
                        label="Provider/Business"
                        name="providerFilter"
                        placeholder="All providers/businesses"
                        required={false}
                        options={providerOptions}
                        optionKey={'id'}
                        optionValue={['first_name', 'last_name']}
                        value={draftProviderId || ''}
                        handleChange={(e) => setDraftProviderId(e.target.value || null)}
                        inputSize={6}
                        labelSize={6}
                        style={{ marginBottom: '8px' }}
                    />

                    {/* Payment Type Filter */}
                    <FormSelectInput
                        label="Payment Type"
                        name="paymentTypeFilter"
                        placeholder="All payment types"
                        required={false}
                        options={paymentTypeOptions}
                        optionKey="id"
                        optionValue={['name']}
                        value={draftPaymentType || ''}
                        handleChange={(e) => setDraftPaymentType(e.target.value || null)}
                        inputSize={6}
                        labelSize={6}
                        style={{ marginBottom: '8px' }}
                    />

                    {/* Status Filter */}
                    <FormSelectInput
                        label="Status"
                        name="statusFilter"
                        placeholder="All statuses"
                        required={false}
                        options={statusOptions}
                        optionKey="id"
                        optionValue={['name']}
                        value={draftStatus || ''}
                        handleChange={(e) => setDraftStatus(e.target.value || null)}
                        inputSize={6}
                        labelSize={6}
                        style={{ marginBottom: '8px' }}
                    />

                    {/* From Date */}
                    <FormTextInput
                        label="From Date"
                        type={'date'}
                        name={'dateFrom'}
                        value={draftDateFrom || ''}
                        handleChange={(e) => setDraftDateFrom(e.target.value || null)}
                        required={false}
                        inputSize={6}
                        labelSize={6}
                        style={{ marginBottom: '8px' }}
                    />

                    {/* To Date */}
                    <FormTextInput
                        label="To Date"
                        type={'date'}
                        name={'dateTo'}
                        value={draftDateTo || ''}
                        handleChange={(e) => setDraftDateTo(e.target.value || null)}
                        required={false}
                        inputSize={6}
                        labelSize={6}
                        style={{ marginBottom: '8px' }}
                    />
                </Grid>
            </FormStyle>
        </FormModal>
    );
};

export default FiltersBar;
